import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, View, Pressable, Platform, ActivityIndicator } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useModelStore, useAgentStore, useLightboxStore } from '@/store';
import { openMessageFile } from '@/constants/utils/files';
import { useVideoPlayer, VideoView } from 'expo-video';
import AudioWaveformPlayer from '@/components/ChatInput/AudioWaveformPlayer';
import Model3DCardContent from '@/components/Model3D/Model3DCardContent';
import MessageInferenceJob from '@/components/Messages/MessageInferenceJob';
import MediaImagePreview from '@/components/Media/MediaImagePreview';
import { BAD_INFERENCE_JOB_STATUSES, isAwaitingRequestedMedia, isMediaOutputFile, extractMessageModelIds, normalizeModelId, resolveDisplayModelIds } from '@/constants/utils/message';
import PropTypes from 'prop-types';
import MediaGenerationWaiting from './MediaGenerationWaiting';
import useChatWaitingGame from '@/hooks/useChatWaitingGame';

// Human-readable label for the placeholder spinner. We don't try to be cute
// here — falling back to "media" keeps us safe for unknown / future kinds.
const GENERATING_MEDIA_LABEL = {
  image: 'Generating image...',
  video: 'Generating video...',
  audio: 'Generating song...',
  '3d': 'Generating 3D model...',
};

// Create styles
const createStyles = (colors, isXS, isHovered, isInteractive = true) => StyleSheet.create({
  container: {
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.solid_color_secondary,
    position: 'relative',
    ...(Platform.OS === 'web' ? {
      // Web: use aspectRatio + min/max for flexible sizing
      aspectRatio: 1,
      minHeight: isXS ? 160 : 200,
      maxHeight: isXS ? 280 : 320,
      // Only look clickable when there's media to open — while generating,
      // the card is a plain placeholder and presses do nothing.
      cursor: isInteractive ? 'pointer' : 'default',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      ...(isInteractive && isHovered && {
        transform: 'scale(1.02)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      }),
    } : {
      // Native: use a fixed height to prevent layout oscillation.
      // aspectRatio + FlashList's maintainVisibleContentPosition causes
      // the layout engine to recalculate on every streaming re-render,
      // producing visible scroll jumps during media generation.
      width: '100%',
      height: isXS ? 200 : 250,
    }),
  },
  audioContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.solid_color_secondary,
    position: 'relative',
    minHeight: 120,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  threeDContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.solid_color_secondary,
    position: 'relative',
    ...(Platform.OS === 'web' ? {
      aspectRatio: 1,
      minHeight: isXS ? 200 : 260,
      maxHeight: isXS ? 320 : 380,
    } : {
      width: '100%',
      height: isXS ? 240 : 300,
    }),
  },
  mediaContent: {
    width: '100%',
    height: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  videoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  playIcon: {
    ...(Platform.OS === 'web' && {
      filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5))',
    }),
  },
  modelBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    right: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    }),
  },
  audioBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: colors.solid_color_block,
  },
  modelLogo: {
    width: 18,
    height: 18,
    borderRadius: 3,
  },
  agentAvatar: {
    width: 18,
    height: 18,
    borderRadius: 9,
  },
  modelName: {
    flex: 1,
  },
  preferenceIcon: {
    marginLeft: 'auto',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  expiredOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_secondary,
  },
  errorBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: 'rgba(254, 0, 0, 0.8)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  audioWrapper: {
    width: '100%',
    padding: 12,
  },
});

// Model badge overlay component
const ModelBadge = React.memo(({ 
  modelIds, 
  agentIds, 
  isPreferred, 
  styles,
  isAudio,
  colors,
}) => {
  const getDeepModelById = useModelStore(state => state.getDeepModelById);
  const getAgentById = useAgentStore(state => state.getAgentById);
  const modelId = normalizeModelId(modelIds?.[0]);
  const modelRecord = useModelStore(state => (modelId ? state.models[modelId] : null));
  const model = modelRecord && modelId ? getDeepModelById(modelId) : null;
  const agent = agentIds?.[0] ? getAgentById(agentIds[0]) : null;
  const item = model || agent;
  const isModel = !!model;

  if (!item) return null;

  const badgeStyle = isAudio ? styles.audioBadge : styles.modelBadge;

  return (
    <View style={badgeStyle}>
      <Image
        source={{ uri: isModel ? item.logoPath : item.avatarPath }}
        style={isModel ? styles.modelLogo : styles.agentAvatar}
        contentFit="cover"
      />
      <Text 
        size="xs" 
        weight="medium" 
        style={[styles.modelName, { color: isAudio ? colors.text_color_primary : '#fff' }]} 
        numberOfLines={1}
      >
        {item.name}
      </Text>
      {isPreferred && (
        <Icon name="star" size={12} color="rgb(35, 200, 45)" style={styles.preferenceIcon} />
      )}
    </View>
  );
});

ModelBadge.displayName = 'ModelBadge';

// Image media card
const ImageMediaContent = React.memo(({ 
  image, 
  styles, 
  onPress,
  isExpired,
}) => {
  if (isExpired) {
    return (
      <View style={styles.expiredOverlay}>
        <Icon name="clock-rotate-left" size={32} color="rgba(150, 150, 150, 0.5)" />
        <Text size="xs" color="tertiary" style={{ marginTop: 8 }}>Image expired</Text>
      </View>
    );
  }

  return (
    <Pressable style={styles.mediaContent} onPress={onPress}>
      <MediaImagePreview
        uri={image.url}
        file={image}
        style={styles.image}
        contentFit="cover"
        transition={200}
        preferThumbnail
      />
    </Pressable>
  );
});

ImageMediaContent.displayName = 'ImageMediaContent';

// Video media card
const VideoMediaContent = React.memo(({
  file,
  styles,
  onPress,
}) => {
  const videoSourceUrl = file?.cdnPath || file?.url || null;
  const videoThumbnailUrl = file?.thumbnailPath || null;

  const player = useVideoPlayer(videoThumbnailUrl ? null : videoSourceUrl, (p) => {
    p.loop = false;
    p.muted = false; // Explicitly enable audio playback on web
    p.pause();
  });

  return (
    <Pressable style={styles.mediaContent} onPress={onPress}>
      {videoThumbnailUrl ? (
        <Image
          source={{ uri: videoThumbnailUrl }}
          style={styles.image}
          contentFit="cover"
        />
      ) : videoSourceUrl ? (
        <VideoView
          player={player}
          style={styles.image}
          nativeControls={false}
          allowsFullscreen={false}
          allowsPictureInPicture={false}
          contentFit="cover"
          crossOrigin="anonymous"
        />
      ) : (
        <View style={[styles.image, { alignItems: 'center', justifyContent: 'center' }]}>
          <Icon name="video" size={48} color="rgba(150, 150, 150, 0.5)" />
        </View>
      )}
      <View style={styles.videoOverlay}>
        <Icon name="play-circle" size={48} color="#fff" style={styles.playIcon} />
      </View>
    </Pressable>
  );
});

VideoMediaContent.displayName = 'VideoMediaContent';

// Audio media card
const AudioMediaContent = React.memo(({ 
  file, 
  styles, 
  colors,
  onPress,
  isXS,
}) => {
  const audioSourceUrl = file?.cdnPath || file?.url || null;
  const audioPlayerRef = React.useRef(null);

  // For audio, we don't want to open lightbox on tap - let the player handle it
  return (
    <View style={styles.audioWrapper}>
      <AudioWaveformPlayer 
        ref={audioPlayerRef} 
        sourceUrl={audioSourceUrl} 
        size={isXS ? 280 : 320} 
        file={file} 
      />
    </View>
  );
});

AudioMediaContent.displayName = 'AudioMediaContent';

function MessageMediaCard({
  message,
  conversationId,
  onPreferResponse,
  onRejectResponse,
  isXS = false,
}) {
  const { colors, theme } = useTheme();
  const canPlayWaitingGame = useChatWaitingGame(conversationId, message.id);
  const [isHovered, setIsHovered] = useState(false);
  const { openLightbox } = useLightboxStore(state => state.actions);
  
  // Only show "Best" badge if user has explicitly selected a preference
  const hasUserSelectedPreference = message.flags?.hasSelectedPreferenceLocally === true;
  const isPreferred = hasUserSelectedPreference && message.flags?.isGroupedResponsePreferred === 1;
  const isError = message.isError || message.hasError;
  const isStreaming = message.isStreaming || message.isTyping || message.isWaiting;
  // Only consider "generating" if actively streaming AND requesting media
  // isRequestingMedia alone shouldn't keep the loading state after streaming ends
  const isGeneratingMedia = !!message.isRequestingMedia && isStreaming;
  // For media-requesting messages (image/video/song/3D), keep the loading UI
  // visible until the artifact actually arrives or an error fires. Some socket
  // events (e.g. early stream_end, async post-processors saving files after
  // stream_end, gpt-image-2 high quality on OpenAI taking 2-5 minutes, Fal
  // async jobs that finish via polling) flip isStreaming/isWaiting to false
  // before the file lands — without this guard the placeholder vanishes and
  // the chat looks blank for tens of seconds, making users think generation
  // silently failed.
  const isWaitingForAsyncMedia = isAwaitingRequestedMedia(message);
  const requestedMediaKind = String(message.isRequestingMedia || '').toLowerCase();
  const generatingLabel = GENERATING_MEDIA_LABEL[requestedMediaKind] || 'Generating media...';

  // Helper to get URL from image object (handles various property names)
  const getImageUrl = (img) => {
    if (!img) return null;
    return img.url || img.cdnPath || img.src || img.uri || img.imageUrl || null;
  };

  // Determine media type - check multiple sources for images
  const firstImage = message.images?.[0];
  const firstImageUrl = getImageUrl(firstImage);
  
  const imageFile = message.files?.find(f => 
    f.fileType === 'image' || f.type?.startsWith?.('image/')
  );
  const videoFile = message.files?.find(f => 
    f.fileType === 'video' || f.type?.startsWith?.('video/')
  );
  const audioFile = message.files?.find(f => 
    f.fileType === 'audio' || f.type?.startsWith?.('audio/')
  );
  const threeDFile = message.files?.find(f => f.fileType === '3d' || isMediaOutputFile(f, '3d'));
  
  // Check if we have valid image content
  const hasValidImage = !!firstImageUrl || !!(imageFile?.cdnPath);
  const isImage = hasValidImage;
  const isVideo = !!videoFile;
  const isAudio = !!audioFile;
  const is3D = !!threeDFile;
  
  // Use the appropriate file for each type
  const mediaFile = imageFile || videoFile || audioFile || threeDFile;
  
  const isExpired = firstImage?.flags?.isExpired || mediaFile?.isExpired;
  
  // Check if we have any displayable media
  const hasDisplayableMedia = isImage || isVideo || isAudio || is3D;
  
  // Check if this is an image model still generating (only while streaming)
  const isImageModelGenerating = message.isImage && !hasDisplayableMedia && isStreaming;

  // Pull out any in-progress / failed inference jobs so we can surface the same
  // status card here that MessageBubble shows. Without this, once `isStreaming`
  // flips to false but the underlying inference job is still pending (common
  // for video and 3D generations that outlive the assistant text stream), the
  // "Generating..." spinner disappears and the card collapses to the "No media"
  // fallback — even though the backend job is still running.
  const pendingInferenceJobs = useMemo(() => {
    if (!Array.isArray(message?.inferences) || message.inferences.length === 0) return [];
    const out = [];
    for (const inf of message.inferences) {
      const jobs = inf?.output?.jobs;
      if (!Array.isArray(jobs)) continue;
      for (const job of jobs) {
        if (BAD_INFERENCE_JOB_STATUSES.has(job?.status)) {
          out.push({ inferenceId: inf.id, job });
        }
      }
    }
    return out;
  }, [message?.inferences]);
  const hasPendingInferenceJobs = pendingInferenceJobs.length > 0;

  // Image/video cards open the lightbox on press; audio and 3D have their
  // own inline interactions, and the generating placeholder does nothing.
  const isCardInteractive = hasDisplayableMedia && !isAudio && !is3D;

  const styles = useMemo(() => createStyles(colors, isXS, isHovered, isCardInteractive), [colors, isXS, isHovered, isCardInteractive]);

  const handlePress = useCallback(() => {
    if (isAudio) return; // Audio has its own player
    if (is3D) return; // 3D card has its own interactive viewer
    
    if (firstImageUrl) {
      openLightbox({ 
        imageUrl: firstImageUrl, 
        conversationId,
      });
    } else if (imageFile?.cdnPath) {
      openLightbox({ 
        imageUrl: imageFile.cdnPath, 
        fileId: imageFile.id,
        conversationId,
      });
    } else if (videoFile?.cdnPath) {
      openMessageFile(videoFile, {
        conversationId,
        onOpenLightbox: ({ imageUrl, fileId }) => {
          openLightbox({
            imageUrl,
            fileId,
            conversationId,
          });
        },
      });
    }
  }, [firstImageUrl, imageFile, videoFile, openLightbox, conversationId, isAudio, is3D]);

  // Pick the appropriate container style for each media type
  let containerStyle = styles.container;
  if (isAudio) containerStyle = styles.audioContainer;
  else if (is3D) containerStyle = styles.threeDContainer;

  if (canPlayWaitingGame && !hasDisplayableMedia && isWaitingForAsyncMedia && !message.flags?.isDeleted && message.role !== 'user') {
    return (
      <View>
        <View style={[styles.container, {
          // Reserve a stable playing area, including on native FlashList.
          // User input and timer ticks must not resize a generating card.
          aspectRatio: undefined, height: 300, minHeight: 300, maxHeight: 300,
        }]}>
          <MediaGenerationWaiting key={`${message.id}:${requestedMediaKind}`} generationKey={`${conversationId}:${message.id}:${requestedMediaKind}`} mediaType={requestedMediaKind} compact />
        </View>
        {pendingInferenceJobs.map(({ inferenceId, job }) => (
          <MessageInferenceJob
            key={`${inferenceId || 'inf'}-${job.id}`}
            conversationId={conversationId}
            messageId={message.id}
            inferenceId={inferenceId}
            job={job}
          />
        ))}
        <ModelBadge
          modelIds={resolveDisplayModelIds({ messageModelIds: extractMessageModelIds(message), displayOverrideIds: message.parameters?.___displayModelIds })}
          agentIds={message.agentIds}
          isPreferred={isPreferred}
          styles={{ ...styles, modelBadge: [styles.modelBadge, { position: 'relative', bottom: 0, left: 0, right: 0, marginTop: 6 }] }}
          isAudio={false}
          colors={colors}
        />
      </View>
    );
  }

  return (
    <View 
      style={containerStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Media content */}
      {isImage && firstImageUrl && (
        <ImageMediaContent
          image={{ ...firstImage, url: firstImageUrl }}
          styles={styles}
          onPress={handlePress}
          isExpired={isExpired}
        />
      )}
      {isImage && !firstImageUrl && imageFile?.cdnPath && (
        <ImageMediaContent
          image={{ url: imageFile.cdnPath }}
          styles={styles}
          onPress={handlePress}
          isExpired={isExpired}
        />
      )}
      {isVideo && videoFile && (
        <VideoMediaContent
          file={videoFile}
          styles={styles}
          onPress={handlePress}
        />
      )}
      {isAudio && audioFile && (
        <AudioMediaContent
          file={audioFile}
          styles={styles}
          colors={colors}
          onPress={handlePress}
          isXS={isXS}
        />
      )}
      {is3D && threeDFile && (
        <Model3DCardContent file={threeDFile} />
      )}
      
      {/* Inference job progress card - shown when an underlying inference job
          is still running (or failed) and we don't yet have media to display.
          This keeps a "Generating..." card visible across the gap between the
          assistant text stream ending and the media file actually arriving
          (especially for long-running video / 3D jobs). */}
      {!hasDisplayableMedia && hasPendingInferenceJobs && (
        <View style={[styles.mediaContent, { padding: 12, justifyContent: 'center' }]}>
          {pendingInferenceJobs.map(({ inferenceId, job }) => (
            <MessageInferenceJob
              key={`${inferenceId || 'inf'}-${job.id || Math.random()}`}
              conversationId={conversationId}
              messageId={message.id}
              inferenceId={inferenceId}
              job={job}
            />
          ))}
        </View>
      )}

      {/* Fallback when no media content is available yet */}
      {!hasDisplayableMedia && !isStreaming && !isGeneratingMedia && !isImageModelGenerating && !hasPendingInferenceJobs && !isWaitingForAsyncMedia && (
        <View style={[styles.mediaContent, { alignItems: 'center', justifyContent: 'center' }]}>
          <Icon name="image" size={32} color={colors.text_color_tertiary} />
          <Text size="xs" color="tertiary" style={{ marginTop: 8 }}>No media</Text>
        </View>
      )}
      
      {/* Generating media indicator - shows when no media yet */}
      {(isGeneratingMedia || isImageModelGenerating || isStreaming || isWaitingForAsyncMedia) && !hasDisplayableMedia && !hasPendingInferenceJobs && (
        <View style={[styles.mediaContent, { alignItems: 'center', justifyContent: 'center' }]}>
          <ActivityIndicator size="large" color={colors.text_color_secondary} />
          <Text size="xs" color="secondary" style={{ marginTop: 8 }}>
            {generatingLabel}
          </Text>
        </View>
      )}

      {/* Loading overlay - only shows on TOP of existing media (skip for 3D so users can interact) */}
      {isStreaming && hasDisplayableMedia && !is3D && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#fff" />
        </View>
      )}

      {/* Error badge */}
      {isError && (
        <View style={styles.errorBadge}>
          <Icon name="circle-exclamation" size={10} color="#fff" />
          <Text size="xs" style={{ color: '#fff' }}>Error</Text>
        </View>
      )}

      {/* Model badge overlay */}
      <ModelBadge
        modelIds={resolveDisplayModelIds({
          messageModelIds: extractMessageModelIds(message),
          displayOverrideIds: message.parameters?.___displayModelIds,
        })}
        agentIds={message.agentIds}
        isPreferred={isPreferred}
        styles={styles}
        isAudio={isAudio}
        colors={colors}
      />
    </View>
  );
}

MessageMediaCard.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    modelIds: PropTypes.arrayOf(PropTypes.string),
    agentIds: PropTypes.arrayOf(PropTypes.string),
    isStreaming: PropTypes.bool,
    isTyping: PropTypes.bool,
    isWaiting: PropTypes.bool,
    isError: PropTypes.bool,
    hasError: PropTypes.bool,
    images: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string,
      flags: PropTypes.shape({
        isExpired: PropTypes.bool,
      }),
    })),
    files: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      cdnPath: PropTypes.string,
      url: PropTypes.string,
      fileType: PropTypes.string,
      type: PropTypes.string,
      thumbnailPath: PropTypes.string,
      isExpired: PropTypes.bool,
    })),
    flags: PropTypes.shape({
      isGroupedResponsePreferred: PropTypes.number,
    }),
    inferences: PropTypes.array,
  }).isRequired,
  conversationId: PropTypes.string.isRequired,
  onPreferResponse: PropTypes.func,
  onRejectResponse: PropTypes.func,
  isXS: PropTypes.bool,
};

MessageMediaCard.displayName = 'MessageMediaCard';

export default React.memo(MessageMediaCard);
